import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navItems = [
  { label: "首页", to: "/" },
  { label: "专题", to: "/special" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !mobileOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
    setMobileCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setKeyword("");
  };

  const textColor = transparent ? "text-background-50" : "text-foreground-800";
  const subText = transparent ? "text-background-50/75" : "text-foreground-500";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur-md border-b border-background-200"
      }`}
    >
      <nav className="w-full px-4 md:px-6 h-16 md:h-[72px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span
            className={`w-9 h-9 rounded-md flex items-center justify-center ${
              transparent ? "bg-background-50/15" : "bg-primary-500"
            }`}
          >
            <i className="ri-film-fill text-lg text-background-50"></i>
          </span>
          <span className={`font-heading text-lg md:text-xl font-bold tracking-wide ${textColor}`}>
            缘之空
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-3 py-2 text-sm rounded-md transition-colors hover:bg-background-200/60 ${
              isHome ? (transparent ? "text-background-50" : "text-primary-600") : textColor
            }`}
          >
            首页
          </Link>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setCategoryOpen((v) => !v)}
              className={`px-3 py-2 text-sm rounded-md transition-colors hover:bg-background-200/60 flex items-center gap-1 whitespace-nowrap cursor-pointer ${textColor}`}
            >
              分类
              <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? "rotate-180" : ""}`}></i>
            </button>
            {categoryOpen && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 border border-background-200 rounded-lg p-2 animate-fade-in">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                  >
                    <i className={`${c.icon} text-base text-primary-500`}></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {navItems.slice(1).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-3 py-2 text-sm rounded-md transition-colors hover:bg-background-200/60 whitespace-nowrap ${textColor}`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className={`hidden lg:flex items-center gap-1 rounded-full border pl-3 pr-1 py-1 w-64 transition-colors ${
              transparent ? "border-background-50/40 bg-background-50/10" : "border-background-300 bg-background-100"
            }`}
          >
            <i className={`ri-search-line text-sm ${subText}`}></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视剧名或剧情"
              className={`flex-1 bg-transparent text-sm outline-none min-w-0 ${
                transparent ? "text-background-50 placeholder:text-background-50/60" : "text-foreground-800 placeholder:text-foreground-400"
              }`}
            />
            <button
              type="submit"
              className="w-7 h-7 rounded-full bg-accent-500 text-foreground-950 flex items-center justify-center hover:bg-accent-400 transition-colors cursor-pointer shrink-0"
              aria-label="搜索"
            >
              <i className="ri-arrow-right-line text-sm"></i>
            </button>
          </form>

          <Link
            to="/search"
            className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors hover:bg-background-200/60 ${textColor}`}
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors hover:bg-background-200/60 ${textColor} cursor-pointer`}
            aria-label="菜单"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 px-4 py-3 animate-fade-in">
          <form onSubmit={submitSearch} className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 pl-3 pr-1 py-1 mb-3">
            <i className="ri-search-line text-sm text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视剧名或剧情"
              className="flex-1 bg-transparent text-sm outline-none min-w-0 text-foreground-800 placeholder:text-foreground-400"
            />
            <button type="submit" className="w-7 h-7 rounded-full bg-accent-500 text-foreground-950 flex items-center justify-center cursor-pointer shrink-0" aria-label="搜索">
              <i className="ri-arrow-right-line text-sm"></i>
            </button>
          </form>
          <div className="flex flex-col">
            <Link to="/" className="px-3 py-3 text-sm text-foreground-700 rounded-md hover:bg-background-100">首页</Link>
            <button
              type="button"
              onClick={() => setMobileCategoryOpen((v) => !v)}
              className="flex items-center justify-between px-3 py-3 text-sm text-foreground-700 rounded-md hover:bg-background-100 cursor-pointer"
            >
              分类
              <i className={`ri-arrow-down-s-line transition-transform ${mobileCategoryOpen ? "rotate-180" : ""}`}></i>
            </button>
            {mobileCategoryOpen && (
              <div className="pl-3 flex flex-col">
                {categories.map((c) => (
                  <Link key={c.slug} to={`/category/${c.slug}`} className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 rounded-md hover:bg-primary-50 hover:text-primary-700">
                    <i className={`${c.icon} text-primary-500`}></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
            {navItems.slice(1).map((item) => (
              <Link key={item.to} to={item.to} className="px-3 py-3 text-sm text-foreground-700 rounded-md hover:bg-background-100">
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}